import { useEffect } from "react";

const isIOSDevice = () => {
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return false;
  }

  return (
    /iP(ad|hone|od)/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

type ScrollLockState = {
  isIOS: boolean;
  scrollY: number;
  body: {
    overflow: string;
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    paddingRight: string;
    overscrollBehavior: string;
  };
  documentElement: {
    overflow: string;
    overscrollBehavior: string;
    height: string;
  };
};

let lockCount = 0;
let scrollLockState: ScrollLockState | null = null;

const lockBodyScroll = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  if (lockCount === 0) {
    const bodyStyle = document.body.style;
    const documentElementStyle = document.documentElement.style;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    scrollLockState = {
      isIOS: isIOSDevice(),
      scrollY: window.scrollY,
      body: {
        overflow: bodyStyle.overflow,
        position: bodyStyle.position,
        top: bodyStyle.top,
        left: bodyStyle.left,
        right: bodyStyle.right,
        width: bodyStyle.width,
        paddingRight: bodyStyle.paddingRight,
        overscrollBehavior: bodyStyle.overscrollBehavior,
      },
      documentElement: {
        overflow: documentElementStyle.overflow,
        overscrollBehavior: documentElementStyle.overscrollBehavior,
        height: documentElementStyle.height,
      },
    };

    bodyStyle.overflow = "hidden";
    bodyStyle.overscrollBehavior = "none";
    bodyStyle.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";

    if (scrollLockState.isIOS) {
      bodyStyle.position = "fixed";
      bodyStyle.top = `-${scrollLockState.scrollY}px`;
      bodyStyle.left = "0";
      bodyStyle.right = "0";
      bodyStyle.width = "100%";
    }

    documentElementStyle.overflow = "hidden";
    documentElementStyle.overscrollBehavior = "none";
    documentElementStyle.height = "100%";
  }

  lockCount += 1;
};

const unlockBodyScroll = () => {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined" ||
    lockCount === 0
  ) {
    return;
  }

  lockCount -= 1;

  if (lockCount > 0 || !scrollLockState) {
    return;
  }

  const { body, documentElement, scrollY } = scrollLockState;
  const bodyStyle = document.body.style;
  const documentElementStyle = document.documentElement.style;

  bodyStyle.overflow = body.overflow;
  bodyStyle.position = body.position;
  bodyStyle.top = body.top;
  bodyStyle.left = body.left;
  bodyStyle.right = body.right;
  bodyStyle.width = body.width;
  bodyStyle.paddingRight = body.paddingRight;
  bodyStyle.overscrollBehavior = body.overscrollBehavior;

  documentElementStyle.overflow = documentElement.overflow;
  documentElementStyle.overscrollBehavior = documentElement.overscrollBehavior;
  documentElementStyle.height = documentElement.height;

  scrollLockState = null;
  window.scrollTo(0, scrollY);
};

export const useBodyScrollLock = (locked: boolean) => {
  useEffect(() => {
    if (!locked) {
      return;
    }

    lockBodyScroll();

    return () => {
      unlockBodyScroll();
    };
  }, [locked]);
};
